/**
 * Single source of truth for Trip categories and difficulty grades.
 * - DB stores canonical English values; UI translates per language.
 * - Ascent grades follow the official EOSH grading: 1, 1+, 2, 2+, … 7, 7+
 *   (https://eosh.gr/wp/climbs/degrees-of-difficulty/). The "+" suffix can be
 *   appended to any grade to indicate scrambling / off-path / exposed sections.
 */

// ---------- EVENT subcategories (sub-tag shown beside the orange "Event" chip) ----------
// Order matters — used as the dropdown order in the CMS Event form.
// Iter154 — "Other" was renamed to "Volunteering" ("Εθελοντική δράση").
// The legacy "Other" key is kept as an alias below so trips persisted
// before the rename still render the new label.
export const EVENT_SUBCATEGORIES = ['Talk', 'Photo/Video Night', 'Workshop', 'Social', 'Open Day', 'Volunteering'];

export const EVENT_SUBCATEGORY_LABELS = {
  Talk:                { en: 'Talk',              gr: 'Ομιλία' },
  'Photo/Video Night': { en: 'Photo/Video Night', gr: 'Προβολή' },
  Workshop:            { en: 'Workshop',          gr: 'Εργαστήριο' },
  Social:              { en: 'Social',            gr: 'Εκδήλωση' },
  'Open Day':          { en: 'Open Day',          gr: 'Ημέρα Γνωριμίας' },
  Volunteering:        { en: 'Volunteering',      gr: 'Εθελοντική δράση' },
  // Legacy aliases — old data still renders correctly
  'Open House':        { en: 'Open Day',          gr: 'Ημέρα Γνωριμίας' },
  Other:               { en: 'Volunteering',      gr: 'Εθελοντική δράση' },
};

export const tEventSubcategory = (value, lang) => {
  const e = EVENT_SUBCATEGORY_LABELS[value];
  if (!e) return value || '';
  return lang === 'gr' ? e.gr : e.en;
};

// ---------- ASCENT (expedition) categories ----------
export const ASCENT_CATEGORIES = ['Hymettus', 'Greece', 'Abroad'];

export const ASCENT_CATEGORY_LABELS = {
  All:      { en: 'All',      gr: 'Όλα' },
  Hymettus: { en: 'Hymettus', gr: 'Υμηττός' },
  Greece:   { en: 'Greece',   gr: 'Ελλάδα' },
  Abroad:   { en: 'Abroad',   gr: 'Εξωτερικό' },
  Event:    { en: 'Event',    gr: 'Εκδήλωση' },
};

// ---------- COURSE (class) categories ----------
export const COURSE_CATEGORIES = ['Climbing', 'Mountaineering', 'Alpine Ski', 'Ascent Leaders'];

// Legacy / mis-cased aliases written to the DB before we tightened the form
// dropdown. The form auto-coerces these to the canonical value on render so
// the dropdown always reflects what's actually about to be saved.
export const COURSE_CATEGORY_ALIASES = {
  'Mountaineering school': 'Mountaineering',
  'Rock Climbing':         'Climbing',
  'climbing':              'Climbing',
  'mountaineering':        'Mountaineering',
  'alpine ski':            'Alpine Ski',
  'Ski':                   'Alpine Ski',
  'Ski Touring':           'Alpine Ski',
  'Expedition Leaders':    'Ascent Leaders',
};

export const COURSE_CATEGORY_LABELS = {
  All:              { en: 'All',             gr: 'Όλες' },
  Climbing:         { en: 'Climbing',        gr: 'Αναρρίχηση' },
  Mountaineering:   { en: 'Mountaineering',  gr: 'Ορειβασία' },
  'Alpine Ski':     { en: 'Alpine Ski',      gr: 'Ορειβατικό Σκι' },
  'Ascent Leaders': { en: 'Ascent Leaders',  gr: 'Αρχηγοί Αναβάσεων' },
};

// ---------- ASCENT difficulty grades (EOSH numeric scheme) ----------
// Base grades; "+" suffix is allowed on any grade to indicate scrambling / exposed sections.
export const ASCENT_GRADE_BASES = ['1', '2', '3', '4', '5', '6', '7'];
export const ASCENT_GRADES = ASCENT_GRADE_BASES.flatMap((g) => [g, `${g}+`]);

// Full EOSH descriptions for tooltips / legend (Greek source = canonical)
export const ASCENT_GRADE_DESCRIPTIONS = {
  1: {
    gr: 'Εύκολη πεζοπορία διάρκειας έως ~6 ωρών, υψομετρική διαφορά έως 400-500μ. Κατάλληλη για αρχάριους.',
    en: 'Easy hike up to ~6 hours, elevation gain up to 400-500m. Suitable for beginners.',
  },
  2: {
    gr: 'Πεζοπορία έως ~8 ώρες, υψομετρική διαφορά έως ~1000μ. Πιθανά τεχνικά περάσματα ή τμήματα εκτός μονοπατιού.',
    en: 'Hike up to ~8 hours, elevation gain up to ~1000m. May include technical sections or off-path parts.',
  },
  3: {
    gr: 'Ανάβαση πάνω από 8 ώρες, υψομετρική διαφορά >1000μ ή πολυήμερη. Πιθανά αναρριχητικά περάσματα ΙΙ-ΙΙΙ UIAA και χρήση ασφαλειών.',
    en: 'Ascent over 8 hours, elevation gain >1000m or multi-day. May include II-III UIAA climbing sections and protection.',
  },
  4: {
    gr: 'Ανάβαση/διάσχιση πάνω από 8 ώρες ή πολυήμερη. Τοπική δυσκολία (αναρριχητικά ΙΙ-ΙΙΙ UIAA), εξοπλισμός διανυκτέρευσης ή/και ασφάλειες.',
    en: 'Multi-day or 8+ hour ascent/traverse. Local difficulty (II-III UIAA), bivy gear and/or protection.',
  },
  5: {
    gr: 'Εύκολη ορειβασία έως ~8 ώρες σε χιονισμένο πεδίο. Απαραίτητος εξοπλισμός χειμερινού βουνού (πιολέ, κραμπόν).',
    en: 'Easy mountaineering up to ~8 hours on snow. Winter mountain gear required (ice axe, crampons).',
  },
  6: {
    gr: 'Μέτρια ορειβασία με υψομετρική >1000μ σε χιονισμένο πεδίο. Πιθανά τεχνικά περάσματα. Πλήρης εξοπλισμός χειμερινού βουνού.',
    en: 'Moderate mountaineering, elevation >1000m on snow. Possible technical sections. Full winter gear.',
  },
  7: {
    gr: 'Δύσκολη ορειβατική διαδρομή σε χιονισμένο πεδίο. Χρήση ασφαλειών, εξοπλισμός διανυκτέρευσης, πιθανές διασχίσεις. Απαιτεί εμπειρία.',
    en: 'Hard mountaineering route on snow. Protection, bivy gear, possible traverses. Experience required.',
  },
};

// "+" indicates scrambling / off-path / exposed sections on top of the base grade.
export const ASCENT_GRADE_PLUS_NOTE = {
  gr: '+ : περιλαμβάνει σκαρφάλωμα, τμήματα εκτός μονοπατιού ή εκτεθειμένα περάσματα.',
  en: '+ : includes scrambling, off-path or exposed sections.',
};

// ---------- COURSE difficulty levels ----------
export const COURSE_LEVELS = ['Beginners', 'Intermediate'];
export const COURSE_LEVEL_LABELS = {
  All:          { en: 'All Levels',    gr: 'Όλα τα Επίπεδα' },
  Beginners:    { en: 'Beginners',     gr: 'Αρχάριοι' },
  Intermediate: { en: 'Intermediate',  gr: 'Μέσου Επιπέδου' },
};

// ---------- Helpers ----------
export const tCategory = (kind, value, lang) => {
  const map = kind === 'class' ? COURSE_CATEGORY_LABELS : ASCENT_CATEGORY_LABELS;
  // For courses, fold legacy aliases (e.g. "Mountaineering school") onto
  // their canonical key so badges keep rendering the correct Greek/English
  // labels even if the DB still holds a pre-iter65 value.
  const folded = kind === 'class' ? (COURSE_CATEGORY_ALIASES[value] || value) : value;
  const entry = map[folded];
  if (!entry) return folded;
  return lang === 'gr' ? entry.gr : entry.en;
};

export const tCourseLevel = (value, lang) => {
  const e = COURSE_LEVEL_LABELS[value];
  if (!e) return value;
  return lang === 'gr' ? e.gr : e.en;
};

export const baseAscentGrade = (g) => (g || '').replace(/\+$/, '');


// ---- Event location presets ----
// Add new presets here when needed; backend stores the preset key only.
export const EVENT_LOCATION_PRESETS = {
  eosh_office: {
    label_en: 'EOSH Office',
    label_gr: 'Γραφεία ΕΟΣΗ',
    maps_url: 'https://www.google.com/maps/search/?api=1&query=Ε.Ο.Σ.+Ηλιούπολης',
  },
};

/** Resolve trip → {label, maps_url} based on preset key OR custom fields. */
export const resolveEventLocation = (trip, lang = 'en') => {
  if (!trip) return null;
  const preset = trip.location_preset && EVENT_LOCATION_PRESETS[trip.location_preset];
  if (preset) {
    return {
      label: lang === 'gr' ? preset.label_gr : preset.label_en,
      maps_url: preset.maps_url,
    };
  }
  const custom_label = lang === 'gr'
    ? (trip.location_label_gr || trip.location_label || '')
    : (trip.location_label || trip.location_label_gr || '');
  if (!custom_label && !trip.location_maps_url) return null;
  return {
    label: custom_label,
    maps_url: trip.location_maps_url || '',
  };
};

// Iter355 — The competitive Sports/Disciplines/Competition taxonomy now lives
// in its own trip-independent module (`sportsTaxonomy.js`). Re-exported here
// for backward compatibility so existing `tripsTaxonomy` imports keep working.
// New code should import from `../data/sportsTaxonomy` directly.
export {
  CHAMPIONSHIP_SPORTS,
  CHAMPIONSHIP_DISCIPLINES,
  COMPETITION_TYPES,
  tChampionshipSport,
  tChampionshipDiscipline,
  tCompetitionType,
} from './sportsTaxonomy';
